import { getBrowserBridgeAPI } from '@atlassian/rovo-content-bridge-api/singleton';
import {
	createNativeEmbedChildClient,
	type OnReadContentCallback,
} from '@atlassian/native-embeds-core/services/client/child/main';
import type { NativeEmbedBridgeClientQueryParams } from '@atlassian/native-embeds-core/services/client/iframe-integration/main';
import {
	NativeEmbedCoreFeature,
	type NativeEmbedFeature,
} from '@atlassian/native-embeds-core/types/features/main';

import { fg } from '@confluence/feature-gating';

/**
 * One reference-counted native-embed child client per iframe bridge identity.
 *
 * The Rovo content bridge keeps a single handler per `childClientId:command` key, and `destroy()`
 * tears down every handler for that id. A second client — or an early destroy — would clobber
 * handlers (notably `read-content`) that other mounted consumers still need. So the client is owned
 * here, not by any hook consumer: consumers lease it and the last release destroys it. See the
 * shared-client invariants in AGENTS.md.
 */

export type NativeEmbedChildClient = ReturnType<typeof createNativeEmbedChildClient>;

export type CreateNativeEmbedChildClient = typeof createNativeEmbedChildClient;

export type NativeEmbedChildClientLease = {
	client: NativeEmbedChildClient;
	release: () => void;
};

/**
 * Which surface answers a `read-content` command. The editor is authoritative for the page it has
 * open (including a view-only live doc, which has no renderer body), so `editor` beats `renderer`.
 * Resolving by source rather than registration order stays deterministic while both are briefly
 * mounted during a mode or route transition.
 */
export type ReadPageContentSource = 'renderer' | 'editor';

export type ReadPageContent = {
	contentId: string;
	source: ReadPageContentSource;
	getPageAdf: () => string | Promise<string>;
};

type ReadPageContentRegistration = ReadPageContent & { id: number };

/**
 * How a read attempt failed. `stale` means the embed navigated away before the read settled;
 * `timeout` means a read dispatched to this live child never settled (the bridge's queue lifetime
 * only bounds messages still awaiting a receiver — see {@link READ_CONTENT_TIMEOUT_IN_MILLIS}).
 */
export type NativeEmbedReadFailureReason = 'read-failed' | 'stale' | 'timeout';

/**
 * Classified read failure. Rejecting with this (instead of resolving an empty body) is how a read
 * failure crosses the bridge: only `message` survives the postMessage boundary, so the reason is
 * encoded there for host-side observability.
 */
export class NativeEmbedReadError extends Error {
	readonly reason: NativeEmbedReadFailureReason;

	constructor(reason: NativeEmbedReadFailureReason) {
		super(`Native embed read failed: ${reason}`);
		this.name = 'NativeEmbedReadError';
		this.reason = reason;
	}
}

// Safety net for a read already dispatched to this live child that never settles. Matches the
// bridge's message-queue lifetime.
const READ_CONTENT_TIMEOUT_IN_MILLIS = 90_000;

const withReadContentTimeout = (value: string | Promise<string>): Promise<string> =>
	new Promise<string>((resolve, reject) => {
		const timer = setTimeout(() => {
			reject(new NativeEmbedReadError('timeout'));
		}, READ_CONTENT_TIMEOUT_IN_MILLIS);

		Promise.resolve(value).then(
			(resolved) => {
				clearTimeout(timer);
				resolve(resolved);
			},
			(error) => {
				clearTimeout(timer);
				reject(error);
			},
		);
	});

const SOURCE_PRIORITY: Record<ReadPageContentSource, number> = {
	renderer: 0,
	editor: 1,
};

type SharedNativeEmbedChildClient = NativeEmbedBridgeClientQueryParams & {
	client: NativeEmbedChildClient;
	consumerCount: number;
};

let sharedClient: SharedNativeEmbedChildClient | null = null;
let registrations: ReadPageContentRegistration[] = [];
let nextRegistrationId = 0;

const resolveReadPageContentOwner = (): ReadPageContentRegistration | undefined =>
	registrations.reduce<ReadPageContentRegistration | undefined>(
		(owner, candidate) =>
			owner && SOURCE_PRIORITY[candidate.source] < SOURCE_PRIORITY[owner.source]
				? owner
				: candidate,
		undefined,
	);

const setupReadContentDispatch = (client: NativeEmbedChildClient): void => {
	const dispatch = async () => {
		const owner = resolveReadPageContentOwner();

		if (!owner) {
			return undefined;
		}

		const { contentId, getPageAdf } = owner;

		let embeddedPageBody: string;
		try {
			embeddedPageBody = await withReadContentTimeout(getPageAdf());
		} catch (error) {
			// Preserve an already-classified reason (e.g. timeout) rather than relabelling it.
			throw error instanceof NativeEmbedReadError ? error : new NativeEmbedReadError('read-failed');
		}

		if (resolveReadPageContentOwner()?.id !== owner.id) {
			// The embed navigated away while we awaited, so the requested page's content can no longer be answered.
			throw new NativeEmbedReadError('stale');
		}

		return {
			contentType: 'embeddedPage' as const,
			embeddedPageBody,
			contentId,
		};
	};

	client.onReadContent(dispatch as OnReadContentCallback);
};

const disposeSharedClient = (): void => {
	sharedClient?.client.destroy();
	sharedClient = null;
	registrations = [];
};

const leaseSharedClient = (
	ownedClient: SharedNativeEmbedChildClient,
): NativeEmbedChildClientLease => {
	ownedClient.consumerCount += 1;
	let released = false;

	return {
		client: ownedClient.client,
		release: () => {
			if (released) {
				return;
			}
			released = true;

			if (sharedClient !== ownedClient) {
				return;
			}

			ownedClient.consumerCount -= 1;
			if (ownedClient.consumerCount === 0) {
				disposeSharedClient();
			}
		},
	};
};

/** Leases the shared client for `identity`, creating it on first acquire. */
export const acquireSharedNativeEmbedChildClient = (
	identity: NativeEmbedBridgeClientQueryParams,
	createClient: CreateNativeEmbedChildClient = createNativeEmbedChildClient,
): NativeEmbedChildClientLease => {
	const { childClientId, parentClientId } = identity;

	if (sharedClient) {
		if (
			sharedClient.childClientId === childClientId &&
			sharedClient.parentClientId === parentClientId
		) {
			return leaseSharedClient(sharedClient);
		}

		disposeSharedClient();
	}

	const client = createClient(
		getBrowserBridgeAPI(),
		childClientId,
		parentClientId,
		new Set<NativeEmbedFeature>([NativeEmbedCoreFeature.NotifyHostClick]),
	);
	sharedClient = { childClientId, parentClientId, client, consumerCount: 0 };

	// oxlint-disable-next-line confluence-feature-gating/no-new-feature-gates
	if (fg('maui_remixable_page_native_embeds')) {
		setupReadContentDispatch(client);
	}

	return leaseSharedClient(sharedClient);
};

/**
 * Registers a candidate read-content owner and returns its unregister. Registrations live here, not
 * on the client, so a provider can register before the client exists and unmounting one consumer
 * can't disturb another.
 */
export const registerReadPageContent = (readPageContent: ReadPageContent): (() => void) => {
	nextRegistrationId += 1;
	const registration: ReadPageContentRegistration = {
		...readPageContent,
		id: nextRegistrationId,
	};
	registrations = [...registrations, registration];

	return () => {
		registrations = registrations.filter(({ id }) => id !== registration.id);
	};
};

export const resetSharedNativeEmbedChildClientForTesting = (): void => {
	disposeSharedClient();
};
